import { denormalize } from 'normalizr';
import { createSelector } from 'reselect';
import {
  initiative as initiativeSchema,
  initiativeParticipation as initiativeParticipationSchema,
  organization as organizationSchema,
} from '../lib/schema.js';
import { CALL_API, HTTP_METHODS } from '../middlewares/api.js';
import { selectEntities } from './entities.js';

// API Actions
const CREATE_INITIATIVE_REQUEST =
  'changesync/initiatives/CREATE_INITIATIVE_REQUEST';
const CREATE_INITIATIVE_SUCCESS =
  'changesync/initiatives/CREATE_INITIATIVE_SUCCESS';
const CREATE_INITIATIVE_FAILURE =
  'changesync/initiatives/CREATE_INITIATIVE_FAILURE';
const RETRIEVE_INITIATIVE_REQUEST =
  'changesync/initiatives/RETRIEVE_INITIATIVE_REQUEST';
const RETRIEVE_INITIATIVE_SUCCESS =
  'changesync/initiatives/RETRIEVE_INITIATIVE_SUCCESS';
const RETRIEVE_INITIATIVE_FAILURE =
  'changesync/initiatives/RETRIEVE_INITIATIVE_FAILURE';
const RETRIEVE_INITIATIVE_PREVIEW_REQUEST =
  'changesync/initiatives/RETRIEVE_INITIATIVE_PREVIEW_REQUEST';
const RETRIEVE_INITIATIVE_PREVIEW_SUCCESS =
  'changesync/initiatives/RETRIEVE_INITIATIVE_PREVIEW_SUCCESS';
const RETRIEVE_INITIATIVE_PREVIEW_FAILURE =
  'changesync/initiatives/RETRIEVE_INITIATIVE_PREVIEW_FAILURE';
const UPDATE_INITIATIVE_REQUEST =
  'changesync/initiatives/UPDATE_INITIATIVE_REQUEST';
const UPDATE_INITIATIVE_SUCCESS =
  'changesync/initiatives/UPDATE_INITIATIVE_SUCCESS';
const UPDATE_INITIATIVE_FAILURE =
  'changesync/initiatives/UPDATE_INITIATIVE_FAILURE';
const DELETE_INITIATIVE_REQUEST =
  'changesync/initiatives/DELETE_INITIATIVE_REQUEST';
const DELETE_INITIATIVE_SUCCESS =
  'changesync/initiatives/DELETE_INITIATIVE_SUCCESS';
const DELETE_INITIATIVE_FAILURE =
  'changesync/initiatives/DELETE_INITIATIVE_FAILURE';
const RETRIEVE_ANCESTRY_REQUEST =
  'changesync/initiatives/RETRIEVE_ANCESTRY_REQUEST';
const RETRIEVE_ANCESTRY_SUCCESS =
  'changesync/initiatives/RETRIEVE_ANCESTRY_SUCCESS';
const RETRIEVE_ANCESTRY_FAILURE =
  'changesync/initiatives/RETRIEVE_ANCESTRY_FAILURE';
const RETRIEVE_PARTICIPATIONS_REQUEST =
  'changesync/initiatives/RETRIEVE_PARTICIPATIONS_REQUEST';
const RETRIEVE_PARTICIPATIONS_SUCCESS =
  'changesync/initiatives/RETRIEVE_PARTICIPATIONS_SUCCESS';
const RETRIEVE_PARTICIPATIONS_FAILURE =
  'changesync/initiatives/RETRIEVE_PARTICIPATIONS_FAILURE';
const SEARCH_PARTICIPATIONS_REQUEST =
  'changesync/initiatives/SEARCH_PARTICIPATIONS_REQUEST';
const SEARCH_PARTICIPATIONS_SUCCESS =
  'changesync/initiatives/SEARCH_PARTICIPATIONS_SUCCESS';
const SEARCH_PARTICIPATIONS_FAILURE =
  'changesync/initiatives/SEARCH_PARTICIPATIONS_FAILURE';

// Search Actions
const UPDATE_SEARCH_QUERY = 'changesync/initiatives/UPDATE_SEARCH_QUERY';
const SEARCH_PARTICIPATIONS = 'changesync/initiatives/SEARCH_PARTICIPATIONS';
const CANCEL_SEARCH = 'changesync/initiatives/CANCEL_SEARCH';

export { RETRIEVE_ANCESTRY_SUCCESS };

// Initial State
const initialState = {
  retrieveInitiative: {
    pending: false,
    success: false,
    error: null,
  },
  initiativeParticipationIds: [],
  rootInitiativeParticipationIds: [],
  searchQuery: '',
};

// Reducer
export default (state = initialState, action) => {
  switch (action.type) {
    case RETRIEVE_INITIATIVE_REQUEST:
      return {
        ...state,
        retrieveInitiative: {
          ...state.retrieveInitiative,
          pending: true,
          success: false,
          error: null,
        },
      };
    case RETRIEVE_INITIATIVE_SUCCESS:
      return {
        ...state,
        retrieveInitiative: {
          ...state.retrieveInitiative,
          pending: false,
          success: true,
        },
      };
    case RETRIEVE_INITIATIVE_FAILURE:
      return {
        ...state,
        retrieveInitiative: {
          ...state.retrieveInitiative,
          pending: false,
          error: action.error,
        },
      };

    case RETRIEVE_ANCESTRY_SUCCESS:
      return {
        ...state,
        rootInitiativeParticipationIds: action.payload.result.ancestry,
      };

    case RETRIEVE_PARTICIPATIONS_SUCCESS:
      return {
        ...state,
        initiativeParticipationIds: action.payload.result,
        searchQuery: initialState.searchQuery,
      };

    case SEARCH_PARTICIPATIONS_SUCCESS:
      return {
        ...state,
        initiativeParticipationIds: action.payload.result,
      };

    case UPDATE_SEARCH_QUERY:
      return {
        ...state,
        searchQuery: action.payload,
      };

    default:
      return state;
  }
};

// Action Creators
/**
 * Retrieve an Initiative given its type and ID.
 *
 * @param type {string} One of: communication, readiness, training
 * @param id {number} ID of the Initiative to retrieve
 * @param disabledCamelizeKeys prevent transform form_data to fromData ...etc. (initiative from only)
 * @return {{}} Redux action to be handle by API middleware
 */
export const retrieveInitiative = (type, id, disabledCamelizeKeys = false) => ({
  [CALL_API]: {
    types: [
      RETRIEVE_INITIATIVE_REQUEST,
      RETRIEVE_INITIATIVE_SUCCESS,
      RETRIEVE_INITIATIVE_FAILURE,
    ],
    endpoint: `${type}_initiatives/${id}`,
    method: HTTP_METHODS.GET,
    schema: initiativeSchema,
    disabledCamelizeKeys,
  },
});

export const createInitiative = (type, formData) => ({
  [CALL_API]: {
    types: [
      CREATE_INITIATIVE_REQUEST,
      CREATE_INITIATIVE_SUCCESS,
      CREATE_INITIATIVE_FAILURE,
    ],
    endpoint: `${type}_initiatives`,
    method: HTTP_METHODS.POST,
    body: formData,
    schema: initiativeSchema,
    disabledCamelizeKeys: true,
  },
});

/**
 * Update an Initiative given a FormData object from the submitted form.
 * FormData is used to include the CSV file.
 *
 * @param type {string} One of: communication, readiness, training
 * @param id {number} ID of the Initiative to update
 * @param formData {FormData}
 * @return {{}} Redux action to be handle by API middleware
 */
export const updateInitiative = (type, id, formData) => ({
  [CALL_API]: {
    types: [
      UPDATE_INITIATIVE_REQUEST,
      UPDATE_INITIATIVE_SUCCESS,
      UPDATE_INITIATIVE_FAILURE,
    ],
    endpoint: `${type}_initiatives/${id}`,
    method: HTTP_METHODS.PUT,
    body: formData,
    schema: initiativeSchema,
    disabledCamelizeKeys: true,
  },
});

export const retrieveInitiativePreview = (type, formData) => ({
  [CALL_API]: {
    types: [
      RETRIEVE_INITIATIVE_PREVIEW_REQUEST,
      RETRIEVE_INITIATIVE_PREVIEW_SUCCESS,
      RETRIEVE_INITIATIVE_PREVIEW_FAILURE,
    ],
    endpoint: `${type}_initiatives/preview`,
    method: HTTP_METHODS.POST,
    body: formData,
    schema: initiativeSchema,
  },
});

export const deleteInitiative = (type, id) => ({
  [CALL_API]: {
    types: [
      DELETE_INITIATIVE_REQUEST,
      DELETE_INITIATIVE_SUCCESS,
      DELETE_INITIATIVE_FAILURE,
    ],
    endpoint: `${type}_initiatives/${id}`,
    method: HTTP_METHODS.DELETE,
  },
});

export const retrieveInitiativeAncestry = (type, id) => ({
  [CALL_API]: {
    types: [
      RETRIEVE_ANCESTRY_REQUEST,
      RETRIEVE_ANCESTRY_SUCCESS,
      RETRIEVE_ANCESTRY_FAILURE,
    ],
    endpoint: `${type}_initiatives/${id}/ancestry`,
    method: HTTP_METHODS.GET,
    schema: {
      organization: organizationSchema,
      ancestry: [initiativeParticipationSchema],
    },
  },
});

export const retrieveInitiativeParticipations =
  (type, initiativeId, initiativeParticipationId) => (dispatch) => {
    const query = {};

    if (initiativeParticipationId) {
      query.initiativeParticipationId = initiativeParticipationId;
    }

    dispatch({
      type: CANCEL_SEARCH,
      meta: {
        debounce: {
          cancel: true,
          key: SEARCH_PARTICIPATIONS,
        },
      },
    });

    return dispatch({
      [CALL_API]: {
        types: [
          RETRIEVE_PARTICIPATIONS_REQUEST,
          RETRIEVE_PARTICIPATIONS_SUCCESS,
          RETRIEVE_PARTICIPATIONS_FAILURE,
        ],
        endpoint: `${type}_initiatives/${initiativeId}/initiative_participations`,
        method: HTTP_METHODS.GET,
        schema: [initiativeParticipationSchema],
        query,
      },
    });
  };

export const searchInitiativeParticipations = (type, initiativeId, query) => ({
  [CALL_API]: {
    types: [
      SEARCH_PARTICIPATIONS_REQUEST,
      SEARCH_PARTICIPATIONS_SUCCESS,
      SEARCH_PARTICIPATIONS_FAILURE,
    ],
    endpoint: `${type}_initiatives/${initiativeId}/initiative_participations/search`,
    method: HTTP_METHODS.GET,
    schema: [initiativeParticipationSchema],
    query: { query },
  },
  meta: {
    debounce: {
      time: 300,
      key: SEARCH_PARTICIPATIONS,
    },
  },
});

export const updateSearchQuery = (query) => ({
  type: UPDATE_SEARCH_QUERY,
  payload: query,
});

// Helpers

const filterInitiativeParticipation =
  (initiative, status, sentiment) => (initiativeParticipation) => {
    if (
      !initiative ||
      !initiative.survey ||
      (status === null && sentiment === null)
    ) {
      return true;
    }

    const { completedAt, leavesAverageSentiment } =
      initiativeParticipation.surveyResponse;

    const surveyEndDate = initiative.survey.endDate;

    const sentimentMatches =
      sentiment === null || leavesAverageSentiment === sentiment;

    let statusMatches =
      status === null || (status === 'pending' && !completedAt);
    if (
      status === 'completed_on_time' &&
      completedAt &&
      completedAt <= surveyEndDate
    ) {
      statusMatches = true;
    } else if (
      status === 'completed_late' &&
      completedAt &&
      completedAt > surveyEndDate
    ) {
      statusMatches = true;
    }

    return sentimentMatches && statusMatches;
  };

// Selectors

export const selectRetrieveInitiativeState = (state) =>
  state.initiatives.retrieveInitiative;

export const selectInitiative = (id, type) =>
  createSelector(selectEntities, (entities) =>
    denormalize(`${type}${id}`, initiativeSchema, entities)
  );

const selectInitiativeParticipationIds = (state) =>
  state.initiatives.initiativeParticipationIds;

export const selectInitiativeParticipations = (
  initiativeId,
  initiativeType,
  initiativeParticipationId,
  status,
  sentiment
) =>
  createSelector(
    selectEntities,
    selectInitiativeParticipationIds,
    selectInitiative(initiativeId, initiativeType),
    (entities, initiativeParticipationIds, initiative) =>
      denormalize(
        initiativeParticipationIds,
        [initiativeParticipationSchema],
        entities
      ).filter(filterInitiativeParticipation(initiative, status, sentiment))
  );

const selectRootInitiativeParticipationIds = (state) =>
  state.initiatives.rootInitiativeParticipationIds;

export const selectAncestryInitiativeParticipations = createSelector(
  selectEntities,
  selectRootInitiativeParticipationIds,
  (entities, rootInitiativeParticipationIds) =>
    denormalize(
      rootInitiativeParticipationIds,
      [initiativeParticipationSchema],
      entities
    )
);

export const selectSearchQuery = (state) => state.initiatives.searchQuery;
